// Pure report/export builders for the isolated G-Reports sandbox feature.
// Composes standings, flight winners, match results, and calcutta payouts from
// a tournament-shaped object via existing node-safe scoring helpers. No React,
// no Firebase, no DB — unit-testable under `node --test`. computePayouts is
// reimplemented here (mirrors data.js) so the test path never pulls Firebase.
import { parsFor, strokeIndexFor, rankTeams, netScoresFor, headToHeadStatus, matchPlayResult } from '../../lib/scoring.js'
import { buildGroups } from './ggData.js'

const teamsList = (flight) => Object.entries(flight?.teams || {}).map(([id, t]) => ({ id, ...t }))

// Normalize a rankTeams row for rendering/CSV.
function normalize(row, i) {
  return {
    teamId: row.id,
    teamName: row.info?.name || row.id,
    players: Array.isArray(row.info?.players) ? row.info.players : [],
    gross: row.gross,
    vsPar: row.vsPar,
    netVsPar: row.netVsPar,
    thru: row.thru,
    place: i + 1,
  }
}

function rankGross(teams, pars) {
  return rankTeams(teams, false, pars).map(normalize)
}

function rankNet(teams, pars, strokeIndex) {
  const netTeams = teams.map((t) => ({ ...t, scores: netScoresFor(t, strokeIndex) }))
  return rankTeams(netTeams, false, pars).map(normalize)
}

// Head-to-head match results from a flight's explicit pairings (deduped).
function flightMatches(flight) {
  const teams = flight?.teams || {}
  const seen = new Set()
  const out = []
  for (const [id, t] of Object.entries(teams)) {
    const oppId = t?.info?.opponentId
    if (!oppId || !teams[oppId] || seen.has(id)) continue
    seen.add(id); seen.add(oppId)
    const a = { id, ...t }
    const b = { id: oppId, ...teams[oppId] }
    const aName = a.info?.name || id
    const bName = b.info?.name || oppId
    const h2h = headToHeadStatus(a, b)
    const mr = matchPlayResult(h2h)
    out.push({
      teamAName: aName,
      teamBName: bName,
      myWins: h2h.myWins,
      oppWins: h2h.oppWins,
      played: h2h.played,
      result: mr.resultText || mr.statusText || 'No result',
      winnerName: mr.winner === 'me' ? aName : mr.winner === 'opp' ? bName : 'All square',
    })
  }
  return out
}

// computePayouts — pure (mirrors data.js). amount = round(pot * pct / 100).
function computePayouts(pot, structure) {
  return (structure || []).map((r) => ({
    place: r.place,
    pct: Number(r.pct) || 0,
    amount: Math.round((Number(pot) || 0) * (Number(r.pct) || 0) / 100),
  }))
}

export function buildReports(sample) {
  const pars = parsFor(sample)
  const strokeIndex = strokeIndexFor(sample)
  const info = sample?.info || {}
  const meta = {
    tournamentName: info.name || 'Tournament',
    courseName: info.course || '',
    date: info.date || '',
    pars,
    strokeIndex,
  }

  const flightsObj = sample?.flights || {}
  const flights = []
  const allTeams = []
  for (const [flightId, flight] of Object.entries(flightsObj)) {
    const list = teamsList(flight)
    allTeams.push(...list)
    const gross = rankGross(list, pars)
    const net = rankNet(list, pars, strokeIndex)
    flights.push({
      flightId,
      flightName: flight?.info?.name || flightId,
      gross,
      net,
      winners: { gross: gross.slice(0, 3), net: net.slice(0, 3) },
      matches: flightMatches(flight),
    })
  }

  const overall = {
    gross: rankGross(allTeams, pars),
    net: rankNet(allTeams, pars, strokeIndex),
  }

  // Calcutta: pot = Σ bids; payout places map to overall gross finishers.
  const bids = sample?.calcutta?.bids || {}
  const structure = sample?.calcutta?.settings?.payoutStructure || []
  const pot = Object.values(bids).reduce((s, b) => s + (Number(b?.amount) || 0), 0)
  const rows = computePayouts(pot, structure).map((p) => {
    const finisher = overall.gross[p.place - 1]
    const teamId = finisher?.teamId
    return {
      place: p.place,
      pct: p.pct,
      amount: p.amount,
      teamName: finisher?.teamName || '—',
      owner: (teamId && bids[teamId]?.owner) || '—',
    }
  })

  return { meta, flights, overall, calcutta: { pot, rows } }
}

// ── CSV exports (client-side; the component hands these to a Blob) ──────────
const csv = (s) => (s == null ? '' : `"${String(s).replace(/"/g, '""')}"`)

export function resultsCsv(reports) {
  const lines = ['Flight,Team,Players,Gross,Vs Par,Net Vs Par,Place']
  for (const f of reports?.flights || []) {
    const netByTeam = Object.fromEntries(f.net.map((r) => [r.teamId, r.netVsPar]))
    f.gross.forEach((r, i) => {
      lines.push([
        csv(f.flightName), csv(r.teamName), csv((r.players || []).join(' / ')),
        r.gross ?? '', r.vsPar ?? '', (netByTeam[r.teamId] ?? ''), i + 1,
      ].join(','))
    })
  }
  return lines.join('\n')
}

export function rosterCsv(sample) {
  const { groups } = buildGroups(sample)
  const lines = ['Flight,Team,Player,Playing Handicap']
  for (const g of groups) {
    for (const p of g.players) {
      lines.push([csv(g.flightName), csv(g.teamName), csv(p.name), p.playingHandicap ?? ''].join(','))
    }
  }
  return lines.join('\n')
}
